import type { InfiniteData } from '@tanstack/react-query';

import type { Conversation } from '@/types';

/**
 * Optimistically prepend a just-created / just-compacted conversation into the
 * first page of the sidebar conversations cache (`useGetConversations`), so the
 * new row appears immediately — before the eventually-consistent
 * `parent_id:self` GSI would return it on the next refetch.
 *
 * Pure + idempotent, so it is safe to call from the create and compact paths
 * and to unit-test in isolation (importing the full `useChatWorker` hook OOMs
 * vitest, so the cache logic lives here):
 *  - No-op (returns `old` unchanged) when the cache has not produced a page yet
 *    (`old` undefined). Reachable only when the initial `useGetConversations`
 *    GET is still in flight (or errored) at the instant of create — a resolved
 *    empty list is `pages:[{data:[]}]`, which is NOT undefined and prepends
 *    fine, and compact runs from an already-loaded conversation. In that narrow
 *    create-only race this cache write contributes nothing — the row still
 *    shows instantly via useChatWorker's render-time overlay (ENG-4382), which
 *    unions the create/compact response into the returned list with no cache
 *    write for a stale GSI refetch to erase.
 *  - Dedups by `key`, so a repeated prepend or a racing WS-INSERT refetch cannot
 *    double-insert the row.
 *
 * Generic over the page shape so the caller's exact `InfinityData<Conversation[]>`
 * page type (offset / cursor / count fields) is preserved without a cast.
 */
export function prependConversationToCache<P extends { data: Conversation[] }>(
  old: InfiniteData<P> | undefined,
  conv: Conversation
): InfiniteData<P> | undefined {
  if (!old) return old;
  const [first, ...restPages] = old.pages;
  if (!first) return old;
  if (first.data.some(c => c.key === conv.key)) return old;
  return {
    ...old,
    pages: [{ ...first, data: [conv, ...first.data] }, ...restPages],
  };
}
